import React, { useMemo } from 'react';
import { Alert, Box, Typography, CircularProgress, Chip } from '@mui/material';
import { useTheme } from 'styled-components';
import { RightSideCards } from '../LeadDetails/LeadDetailsEdit/leadDetailsEdit.styles';
import useFees from '../../hooks/useFees';

const formatAmount = fee => {
  const { feeAmountType, amountMin, amountMax, percentage, amountText } = fee;

  switch (feeAmountType) {
    case 'flat':
      return `$${Number(amountMin).toLocaleString()}`;
    case 'range':
      return `$${Number(amountMin).toLocaleString()} - $${Number(
        amountMax
      ).toLocaleString()}`;
    case 'percentage':
      return `${percentage}%`;
    case 'descriptive':
      return amountText || 'Varies';
    default:
      return 'N/A';
  }
};

const formatFrequency = frequency => {
  const map = {
    monthly: 'Monthly',
    one_time: 'One-time',
    annual: 'Annual',
    weekly: 'Weekly',
    per_occurance: 'Per occurrence'
  };
  return map[frequency] || frequency;
};

const formatTiming = timing => {
  const map = {
    application: 'Application',
    move_in: 'Move-in',
    ongoing: 'Ongoing',
    move_out: 'Move-out',
    situational: 'Situational'
  };
  return map[timing] || timing;
};

const FeeRow = ({ fee, theme }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      py: 0.5
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: '12px',
            color: '#333',
            fontWeight: 500
          }}
        >
          {fee.name}
        </Typography>
        {fee.isRequired && (
          <Chip
            label="Required"
            size="small"
            color="warning"
            sx={{
              fontSize: '9px',
              height: '16px',
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '11px',
          color: '#888',
          mt: 0.25
        }}
      >
        {formatFrequency(fee.frequency)} &middot; {formatTiming(fee.timing)}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 500,
        color: '#333',
        whiteSpace: 'nowrap'
      }}
    >
      {formatAmount(fee)}
    </Typography>
  </Box>
);

const CommunityFees = ({ tenantId, apartmentId }) => {
  const theme = useTheme();

  const {
    data: feesResponse,
    isLoading,
    isError
  } = useFees({
    clientId: tenantId,
    communityId: apartmentId
  });

  const fees = useMemo(() => {
    if (!feesResponse) return [];
    const feeList = Array.isArray(feesResponse)
      ? feesResponse
      : feesResponse?.fees || [];
    return feeList;
  }, [feesResponse]);

  const grouped = useMemo(() => {
    if (!fees.length) return {};
    const groups = {};
    fees.forEach(fee => {
      const key = fee.timing || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(fee);
    });
    return groups;
  }, [fees]);

  const timingOrder = [
    'ongoing',
    'move_in',
    'application',
    'move_out',
    'situational'
  ];

  return (
    <RightSideCards className="right-side-cards">
      <Box sx={{ mb: 2 }}>
        <Typography className="card-title" sx={{ color: theme?.text?.heading }}>
          Community Fees
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.primary
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            Loading fees
          </Typography>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
            color: theme?.text?.error || '#ff4d4f',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
            Failed to load fees
          </Typography>
          <Typography sx={{ fontSize: '12px', mt: 0.625, opacity: 0.8 }}>
            Please try refreshing the page.
          </Typography>
        </Box>
      ) : fees.length > 0 ? (
        <Box>
          {timingOrder
            .filter(timing => grouped[timing]?.length > 0)
            .map(timing => (
              <Box
                key={timing}
                sx={{
                  mb: 2,
                  pb: 1.5,
                  borderBottom: `1px solid ${theme?.border?.primary || '#eee'}`,
                  '&:last-child': { borderBottom: 'none', mb: 0 }
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: theme?.text?.heading,
                    mb: 1
                  }}
                >
                  {formatTiming(timing)}
                </Typography>
                {grouped[timing].map((fee, idx) => (
                  <FeeRow key={fee.id || idx} fee={fee} theme={theme} />
                ))}
              </Box>
            ))}
        </Box>
      ) : (
        <Alert severity="info" sx={{ fontSize: '12px' }}>
          No Fees Recorded in System for Community
        </Alert>
      )}
    </RightSideCards>
  );
};

export default CommunityFees;
